import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import {
  CancellationPill,
  CarAttributePill,
  PickupPill,
  useCarCard,
} from "@tenant/cars";
import { Card, Icon } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./CarCard.module.scss";
import type { VehicleAvailabilityWithSupplier } from "~core-cars/store";
import { useCarsStyles } from "~core-cars/themes";
import { CoreCarsComponent } from "~core-cars/types";

export type CarCardType = {
  car: VehicleAvailabilityWithSupplier;
} & PlatformComponentProps;

const COMPONENT_KEY = CoreCarsComponent.CarCard;

export const CarCard = (props: CarCardType) => {
  const styles = useCarsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { context } = useCarCard(props);
  const { t } = useI18nContext();

  const { className, children } = props;

  return (
    <Card className={clsx(block, className)}>
      <div className={cn("type")}>
        <div className={cn("kind")}>{context.kind}</div>
        <div className={cn("name-container")}>
          {context.useFallbackName ? (
            <>
              <span className={cn("name")}>{context.kind}</span>{" "}
              {t("core-cars.carCard.orSimilar")}
            </>
          ) : (
            <Trans
              i18nKey={t("core-cars.carCard.name", { name: context.name })}
              components={{
                name: <span className={cn("name")} />,
              }}
            />
          )}
        </div>
      </div>

      <img
        className={cn("supplier-image")}
        src={context.supplier.logo}
        alt={context.supplier.name}
      />
      <div className={cn("car-attributes")}>
        {context.attributes.passengersQuantity > 0 ? (
          <CarAttributePill
            icon={<Icon iconName={IconNameEnum.car__passenger} />}
          >
            {t("core-cars.vehicleAttributes.passengers", {
              count: context.attributes.passengersQuantity,
            })}
          </CarAttributePill>
        ) : null}
        <CarAttributePill icon={<Icon iconName={IconNameEnum.car__bags} />}>
          {t("core-cars.vehicleAttributes.bags", {
            count: context.attributes.baggageQuantity,
          })}
        </CarAttributePill>
        <CarAttributePill
          icon={<Icon iconName={IconNameEnum.car__transmission} />}
        >
          {context.attributes.transmissionType}
        </CarAttributePill>
        <CarAttributePill
          className={cn("pill-attributes")}
          icon={<Icon iconName={IconNameEnum.car__airConditioning} />}
        >
          {t("core-cars.vehicleAttributes.airConditioning")}
        </CarAttributePill>
        {context.attributes.unlimitedMileage ? (
          <CarAttributePill
            className={cn("pill-attributes")}
            icon={<Icon iconName={IconNameEnum.car__mileage} />}
          >
            {t("core-cars.vehicleAttributes.mileage.unlimited")}
          </CarAttributePill>
        ) : null}
      </div>
      <div className={cn("rental-attributes")}>
        <PickupPill pickup={context.pickup} />
        <CancellationPill cancellationPolicy={context.cancellationPolicy} />
      </div>
      <div className={cn("divider")} />

      <img
        className={cn("car-image")}
        src={context.image}
        alt={context.useFallbackName ? context.kind : context.name}
      />
      {children}
    </Card>
  );
};
